import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { uploadFile as uploadFileService } from '@/services/uploadService';
import { UploadCloud, File, Loader2, ArrowUp } from 'lucide-react';
import { isAxiosError } from 'axios';
import { analytics } from '@/services/analytics';
import type { OrderItemStatus } from '@/types/orderStatus';

export interface UploadSuccessPayload {
  readonly orderItem: {
    readonly id: string;
    readonly uploadedFileId?: string | null;
    readonly status: OrderItemStatus;
    readonly orderId?: string | null;
    readonly widthIn?: number | null;
    readonly heightIn?: number | null;
    readonly priceUsd?: number | null;
    readonly createdAt: string;
    readonly updatedAt: string;
  };
  readonly file: {
    readonly id: string;
    readonly originalWidthPx?: number;
    readonly originalHeightPx?: number;
    readonly dpiX?: number;
    readonly dpiY?: number;
    readonly widthIn?: number;
    readonly heightIn?: number;
  };
  readonly localFileName: string;
}

interface ImageUploaderProps {
  onUploadSuccess: (payload: UploadSuccessPayload) => void;
  onUploadError: (errorMessage: string) => void;
  onUploadSummary?: (summaryText: string) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadSuccess,
  onUploadError,
  onUploadSummary,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  const getFileKey = (incoming: File) => `${incoming.name}-${incoming.size}-${incoming.lastModified}`;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      return;
    }

    setFiles((prev) => {
      const existingKeys = new Set(prev.map(getFileKey));
      const deduped = acceptedFiles.filter((incoming) => !existingKeys.has(getFileKey(incoming)));
      if (deduped.length > 0) {
        analytics.track('dropzone: ui.upload_files_selected', {
          addedCount: deduped.length,
          totalQueued: prev.length + deduped.length,
        });
      }
      return [...prev, ...deduped];
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.gif', '.tif', '.tiff', '.psd'] },
    multiple: true,
  });

  const getUploadErrorMessage = (error: unknown): string => {
    if (!isAxiosError(error)) {
      return 'An unexpected error occurred.';
    }

    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
      const message = (responseData as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }

    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData.slice(0, 160);
    }

    if (error.response?.status === 413) {
      return 'The file is too large for the upload endpoint.';
    }

    if (error.response?.status) {
      return `Upload failed with server status ${error.response.status}.`;
    }

    return error.message || 'An unexpected error occurred.';
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prev) => prev.filter((existing) => getFileKey(existing) !== getFileKey(fileToRemove)));
    analytics.track('button: ui.upload_file_removed', {
      queuedSize: files.length,
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    analytics.track('button: ui.upload_start', { queuedCount: files.length });
    setIsLoading(true);
    setSummary(null);
    const failedKeys = new Set<string>();
    let uploadedCount = 0;
    let duplicateCount = 0;
    const duplicateNames: string[] = [];
    const otherFailures: { fileName: string; message: string }[] = [];
    let summaryMessage = '';
    let summaryText: string | null = null;

    try {
      for (const currentFile of files) {
        try {
          const response = await uploadFileService(currentFile);
          if (response.duplicate) {
            if (response.orderItem) {
              onUploadSuccess({
                orderItem: response.orderItem,
                file: response.file,
                localFileName: currentFile.name,
              });
              duplicateCount += 1;
              duplicateNames.push(currentFile.name);
              continue;
            }

            duplicateCount += 1;
            duplicateNames.push(currentFile.name);
            continue;
          }

          if (!response.orderItem) {
            const fallbackMessage = t(
              'uploader.unexpected_response',
              'Upload failed due to unexpected server response.',
            );
            onUploadError(`${currentFile.name}: ${fallbackMessage}`);
            failedKeys.add(getFileKey(currentFile));
            otherFailures.push({ fileName: currentFile.name, message: fallbackMessage });
            continue;
          }

          onUploadSuccess({
            orderItem: response.orderItem,
            file: response.file,
            localFileName: currentFile.name,
          });
          uploadedCount += 1;
        } catch (error: unknown) {
          const message = getUploadErrorMessage(error);
          onUploadError(`${currentFile.name}: ${message}`);
          failedKeys.add(getFileKey(currentFile));
          otherFailures.push({ fileName: currentFile.name, message });
        }
      }
      const summaryParts: string[] = [];
      if (uploadedCount > 0) {
        summaryParts.push(
          t('uploader.summary_success', '{{count}} file(s) uploaded successfully.', {
            count: uploadedCount,
          }),
        );
      }
      if (duplicateCount > 0) {
        const duplicatesList = duplicateNames.join(', ');
        summaryParts.push(
          t(
            'uploader.summary_duplicates',
            '{{count}} file(s) skipped because an order item already exists: {{names}}.',
            {
              count: duplicateCount,
              names: duplicatesList,
            },
          ),
        );
      }
      if (otherFailures.length > 0) {
        const failureDetails = otherFailures
          .map(({ fileName, message }) => `${fileName}: ${message}`)
          .join('; ');
        summaryParts.push(
          t('uploader.summary_failures', 'Issues occurred for {{details}}.', {
            details: failureDetails,
          }),
        );
      }
      summaryMessage = summaryParts.join(' ');
      if (summaryMessage) {
        const duplicateLine = duplicateNames.length > 0
          ? `. Duplicates skipped: ${duplicateCount} (${duplicateNames.join(', ')})`
          : duplicateCount > 0
            ? `. Duplicates skipped: ${duplicateCount}`
            : '';
        const failureLine = otherFailures.length > 0
          ? `. Issues: ${otherFailures.map(({ fileName, message }) => `${fileName}: ${message}`).join('; ')}`
          : '';
        summaryText = [
          `New files uploaded: ${uploadedCount}`,
          duplicateLine,
          failureLine,
        ]
          .filter(Boolean)
          .join(' ')
          .replace(/\s+\./g, '.');
      } else {
        summaryText = [
          `New files uploaded: ${uploadedCount}`,
          duplicateCount > 0 ? `. Duplicates skipped: ${duplicateCount}` : '',
        ]
          .filter(Boolean)
          .join(' ')
          .replace(/\s+\./g, '.');
      }
    } finally {
      setFiles((prev) => prev.filter((fileItem) => failedKeys.has(getFileKey(fileItem))));
      setIsLoading(false);
      setSummary(summaryMessage || null);
      const finalSummary = summaryText
        ?? summaryMessage
        ?? t('uploader.summary_default', 'Upload completed. No new files were added.');
      onUploadSummary?.(finalSummary.trim());
      analytics.track('button: ui.upload_complete', {
        uploadedCount,
        duplicateCount,
        failureCount: otherFailures.length,
      });
    }
  };

  return (
    <div className="p-6 rounded-lg shadow-lg w-full max-w-md mx-auto border">
      <h3 className="text-xl font-semibold mb-4">{t('uploader.title')}</h3>
      <div
        {...getRootProps()}
        className={`p-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/50 hover:border-primary'}`}
      >
        <input {...getInputProps()} />
        <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          {isDragActive
            ? t('uploader.drop_active')
            : t('uploader.drop_inactive')}
        </p>
      </div>
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((queuedFile) => (
            <div key={getFileKey(queuedFile)} className="p-2 border rounded-md flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <File className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">{queuedFile.name}</span>
              </div>
              <Button
                type="button"
                onClick={() => handleRemoveFile(queuedFile)}
                variant="ghost"
                size="sm"
                disabled={isLoading}
              >
                X
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button onClick={handleUpload} disabled={files.length === 0 || isLoading} className="w-full mt-4">
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {files.length === 0 ? (
          <span className="inline-flex items-center gap-2">
            <ArrowUp className="h-4 w-4" aria-hidden />
            <span>{t('uploader.press_here_button', 'Press here')}</span>
            <ArrowUp className="h-4 w-4" aria-hidden />
          </span>
        ) : files.length > 1 ? (
          t('uploader.upload_multiple_button', { count: files.length })
        ) : (
          t('uploader.upload_button')
        )}
      </Button>
      {summary && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="upload-summary">
          {summary}
        </p>
      )}
    </div>
  );
};
